import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Typography, Space, DatePicker, Button, message, Tag, Select } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface MovementRow {
  id: number;
  cashRegisterId: number;
  cashRegisterName: string;
  transactionDate: string;
  transactionType: number;
  transactionTypeName: string;
  categoryName: string;
  amount: number;
  balanceAfter: number;
  description?: string | null;
  reference?: string | null;
  documentNo?: string | null;
  invoiceNo?: string | null;
  serviceOrderNo?: string | null;
  serviceOrderCustomerName?: string | null;
  paymentMethodName?: string | null;
  ledgerDescription?: string | null;
}

const CashRegisterMovements: React.FC = () => {
  const isMobile = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const registerId = Number(id);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<MovementRow[]>([]);
  const [registerName, setRegisterName] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(() => {
    const start = dayjs().startOf('month');
    const end = dayjs().endOf('month');
    return [start, end];
  });
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | '1' | '2'>('all');

  const loadData = async () => {
    if (!registerId) return;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { cashRegisterId: registerId };
      if (transactionTypeFilter !== 'all') params.transactionType = Number(transactionTypeFilter);
      if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');

      const [movementsRes, registerRes] = await Promise.all([
        api.get<MovementRow[]>('/cashtransactions', { params }),
        api.get<{ registerName: string }>(`/cashregisters/${registerId}`)
      ]);

      setRows(movementsRes.data || []);
      setRegisterName(registerRes.data?.registerName || '');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Kasa hareketleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerId]);

  useEffect(() => {
    if (!registerId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionTypeFilter]);

  const totalIn = useMemo(
    () => rows.filter(r => r.transactionType === 1).reduce((sum, r) => sum + r.amount, 0),
    [rows]
  );
  const totalOut = useMemo(
    () => rows.filter(r => r.transactionType === 2).reduce((sum, r) => sum + r.amount, 0),
    [rows]
  );
  const net = totalIn - totalOut;

  const parsePaymentTypeFromLedger = (desc?: string | null): string => {
    if (!desc) return '-';
    // Örnekler:
    // "Tahsilat - Nakit - CAR-20260408-0001"
    // "Tedarikçi Ödeme - Kredi Kartı - CAR-20260408-0002"
    const parts = desc.split(' - ').map(x => x.trim()).filter(Boolean);
    if (parts.length >= 3 && (parts[0] === 'Tahsilat' || parts[0] === 'Tedarikçi Ödeme')) {
      return parts[1];
    }
    return '-';
  };

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 140,
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-')
    },
    {
      title: 'Tür',
      dataIndex: 'transactionTypeName',
      key: 'transactionTypeName',
      width: 110,
      render: (_: string, record: MovementRow) => (
        <Tag color={record.transactionType === 1 ? 'green' : 'red'}>
          {record.transactionType === 1 ? 'Giriş' : 'Çıkış'}
        </Tag>
      )
    },
    { title: 'Kategori', dataIndex: 'categoryName', key: 'categoryName', width: 140 },
    {
      title: 'Ödeme Türü',
      key: 'paymentType',
      width: 140,
      render: (_: unknown, r: MovementRow) => r.paymentMethodName || parsePaymentTypeFromLedger(r.ledgerDescription)
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (v: number, r: MovementRow) => (
        <span style={{ color: r.transactionType === 1 ? '#237804' : '#cf1322' }}>
          {v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      )
    },
    {
      title: 'Bakiye Sonrası',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 140,
      align: 'right' as const,
      render: (v: number) => v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    { title: 'Referans', dataIndex: 'reference', key: 'reference', width: 130, render: (v: string | null) => v || '-' },
    {
      title: 'Belge / Servis',
      dataIndex: 'documentNo',
      key: 'documentNo',
      width: 220,
      render: (_: string | null, r: MovementRow) => {
        if (r.serviceOrderNo) {
          return r.serviceOrderCustomerName
            ? `${r.serviceOrderNo} - ${r.serviceOrderCustomerName}`
            : r.serviceOrderNo;
        }
        return r.documentNo || '-';
      }
    },
    { title: 'Fatura', dataIndex: 'invoiceNo', key: 'invoiceNo', width: 130, render: (v: string | null) => v || '-' },
    { title: 'Açıklama', dataIndex: 'description', key: 'description', render: (v: string | null) => v || '-' }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <Space direction="vertical" size={2}>
            <Title level={4} style={{ margin: 0 }}>
              Kasa Hareketleri {registerName ? `- ${registerName}` : ''}
            </Title>
            <Text type="secondary">Seçili kasanın tüm giriş/çıkış hareketleri</Text>
          </Space>
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cash-registers')}>
              Kasalara Dön
            </Button>
            <RangePicker
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
              format="DD.MM.YYYY"
            />
            <Select
              value={transactionTypeFilter}
              onChange={(v) => setTransactionTypeFilter(v)}
              style={{ width: 140 }}
              options={[
                { value: 'all', label: 'Tüm Türler' },
                { value: '1', label: 'Giriş' },
                { value: '2', label: 'Çıkış' }
              ]}
            />
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
              Listele
            </Button>
          </Space>
        </Space>

        <Space style={{ marginBottom: 12 }} wrap>
          <Text strong>Toplam Giriş: {totalIn.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Text>
          <Text strong>Toplam Çıkış: {totalOut.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Text>
          <Text strong>Net: {net.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Text>
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20, showSizeChanger: !isMobile, showTotal: t => `Toplam ${t} kayıt` }}
        />
      </Card>
    </div>
  );
};

export default CashRegisterMovements;
